import React, { useState } from 'react';
import { IoArrowRedoOutline } from 'react-icons/io5';
import { XIcon } from '@heroicons/react/solid';
import { FaLock } from 'react-icons/fa';
import { useRouter } from 'next/router';

import Link from 'components/Link';
import PulseLoader from 'components/Loading/Pulse';
import Modal from 'components/Modal/HeadlessUiModal';

import useViewer from 'hooks/useViewer';

import { AuthStatus } from 'constants/auth';
import { LOGIN_PAGE, SIGNUP_PAGE } from 'constants/pages';
import {
  useInsertFlowConfigurationsOneMutation,
} from 'types/generated/client';


interface SaveFilterModalProps {
  isOpen: boolean;
  handleClose: () => void;
  userId: string | null;
  currentFilters: Record<string, any>;
  refetchFlow: () => void;
}

export const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  isOpen,
  handleClose,
  userId,
  currentFilters,
  refetchFlow,
}) => {
  const viewer = useViewer();
  const router = useRouter();

  const [filterName, setFilterName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [strategyNameError, setStrategyNameError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [insertFlowConfig, { loading }] = useInsertFlowConfigurationsOneMutation();

  const isLocked = !userId;
  const currentPath = router.asPath;

  const validateStrategyName = (name: string): boolean => {
    if (!name.trim()) {
      setStrategyNameError('Filter Name is required');
      return false;
    } else {
      setStrategyNameError('');
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateStrategyName(filterName)) {
      return;
    }

    setStrategyNameError('');
    try {
      await insertFlowConfig({
        variables: {
          object: {
            userId,
            title: filterName,
            description: notes || 'Flow for saved strategy',
            configuration: currentFilters,
          },
        },
      });
      refetchFlow();
      setIsSuccess(true);
      setFilterName('');
      setNotes('');
      setTimeout(() => {
        handleClose();
        setIsSuccess(false);
      }, 500);
    } catch (err) {
      console.error('Save flow filter failed:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-lg">
      <div className="relative rounded-lg bg-palette-black-1 p-3">
        <button onClick={() => handleClose()} className="absolute right-3 top-2">
          <XIcon className="w-5" />
        </button>
        <div className="h-full p-3">
          {isLocked ? (
            <div className="flex justify-center">
              <FaLock className="mr-2" />
            </div>
          ) : null}
          {viewer.status !== AuthStatus.User ? (
            <div className="text-center text-lg font-semibold">Saved Filters locked</div>
          ) : (
            <div className="flex justify-center text-4xl font-semibold">Save Filters</div>
          )}
          <div className="mt-2">
            {viewer.status !== AuthStatus.User ? (
              <>
                <div className="text-pretty text-center text-lg font-normal text-brand-green-primary">
                  Create a free account or
                  <Link href={`${LOGIN_PAGE}?next=${currentPath}`} className="px-1 underline">
                    login
                  </Link>
                  to save your Filters.
                </div>
                <div className="flex justify-center gap-2 py-4">
                  <Link
                    role="button"
                    href={`${SIGNUP_PAGE}?next=${currentPath}`}
                    className="flex items-center justify-center rounded-md  border border-transparent bg-brand-primary px-8 py-2 text-base font-medium text-white no-underline"
                  >
                    <span className="mr-2">Get Free Access</span>
                    <IoArrowRedoOutline />
                  </Link>
                </div>
              </>
            ) : (
              <div className="mt-4">
                {loading ? (
                  <div className="flex w-full justify-center">
                    <PulseLoader />
                  </div>
                ) : isSuccess ? (
                  <div className="mt-4 text-center text-brand-green-primary">
                    Strategy saved successfully!
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="Strategy"
                        className="block text-sm font-medium leading-6 text-white"
                      >
                        Filter Name
                      </label>
                      <div className="mt-2">
                        <input
                          id="strategyName"
                          name="strategyName"
                          type="text"
                          value={filterName}
                          onChange={(e) => {
                            setFilterName(e.target.value);
                            if (strategyNameError) validateStrategyName(e.target.value);
                          }}
                          onBlur={() => validateStrategyName(filterName)}
                          placeholder="Enter Filter name"
                          className="mb-2.5 block h-12 w-full rounded border-2 border-transparent bg-palette-black-4 p-2.5 px-4 text-sm font-medium leading-relaxed text-white transition-colors duration-300 hover:border-brand-primary focus:border-brand-primary focus:outline-none"
                        />
                        {strategyNameError && (
                          <p className="mt-1 text-sm text-red-500">{strategyNameError}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="Notes"
                        className="block text-sm font-medium leading-6 text-white"
                      >
                        Filter Notes
                      </label>
                      <div className="mt-2">
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          placeholder="Optional: Enter any notes here"
                          className="mb-2.5 block w-full rounded border-2 border-transparent bg-palette-black-4 p-2.5 px-4 text-sm font-medium leading-relaxed text-white transition-colors duration-300 hover:border-brand-primary focus:border-brand-primary focus:outline-none"
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="flex w-full items-center justify-center rounded-md bg-brand-primary px-2.5 py-3 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                    >
                      Save Filters
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SaveFilterModal;
